import axios from 'axios';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('admin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh token on 401 responses
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject: (err: any) => reject(err),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const storedRefreshToken = localStorage.getItem('admin_refresh_token');
        if (!storedRefreshToken) throw new Error('No refresh token');

        const { data } = await api.post('/auth/refresh', { refreshToken: storedRefreshToken });
        localStorage.setItem('admin_token', data.token);
        localStorage.setItem('admin_refresh_token', data.refreshToken);
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        processQueue(null, data.token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// --- GET de-duplication + short-lived cache --------------------------------
// On the homepage many components fetch the same public endpoints at once (and
// React StrictMode double-fires effects in dev), flooding the network and delaying
// the LCP image. This collapses identical in-flight GETs into a single request and
// serves repeats from a short TTL cache. Only used for public read endpoints.
type CacheEntry = { res: any; ts: number };
const GET_TTL = 20_000;
const getCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<any>>();

const cacheKey = (url: string, params?: any) => url + (params ? '?' + JSON.stringify(params) : '');

const cachedGet = (url: string, config?: any) => {
  const key = cacheKey(url, config?.params);
  const hit = getCache.get(key);
  if (hit && Date.now() - hit.ts < GET_TTL) return Promise.resolve(hit.res);
  const pending = inFlight.get(key);
  if (pending) return pending;
  const p = api
    .get(url, config)
    .then((res) => {
      getCache.set(key, { res, ts: Date.now() });
      inFlight.delete(key);
      return res;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, p);
  return p;
};

// Drop cached public reads (e.g. after an admin edit / cms-updated event).
export const clearApiCache = () => {
  getCache.clear();
  inFlight.clear();
};

// Coupons
export const getCoupons = (params?: any) => cachedGet('/public/coupons/list', { params });
export const getCouponById = (id: string) => api.get(`/public/coupons/details/${id}`);
export const getAdminCoupons = () => api.get('/admin/coupons/list');
export const createCoupon = (data: any) => api.post('/admin/coupons/create', data);
export const updateCoupon = (id: string, data: any) => api.put(`/admin/coupons/update/${id}`, data);
export const deleteCoupon = (id: string) => api.delete(`/admin/coupons/delete/${id}`);
export const trackClick = (id: string) => api.post(`/public/coupons/track-click/${id}`);
export const searchCoupons = (query: string) => api.get('/public/coupons/search', { params: { query } });

// Stores
export const getStores = (params?: any) => cachedGet('/public/stores/list', { params });
export const trackStoreClick = (id: string) => api.post(`/public/stores/track-click/${id}`);
export const getAdminStores = (params?: any) => api.get('/admin/stores/list', { params });
export const getStoreBySlug = (slug: string) => api.get(`/public/stores/details/${slug}`);
export const createStore = (data: any) => api.post('/admin/stores/create', data);
export const updateStore = (id: string, data: any) => api.put(`/admin/stores/update/${id}`, data);
export const deleteStore = (id: string, deleteCoupons?: boolean) => api.delete(`/admin/stores/delete/${id}${deleteCoupons ? '?deleteCoupons=true' : ''}`);

// CMS / Site Config
export const getSiteConfig = () => api.get('/public/site/config');
export const updateSiteConfig = (data: any) => api.put('/admin/pages/site-config/update', data);
export const getNavigation = () => api.get('/public/navbar/navigation');
export const updateNavigation = (data: any) => api.put('/admin/navbar/navigation/update', data);

// Navbar Menu Items
export const getNavbarItems = () => api.get('/admin/navbar');
export const getPublicNavbarItems = () => cachedGet('/public/navbar/items');
export const createNavbarItem = (data: any) => api.post('/admin/navbar/create', data);
export const updateNavbarItem = (id: string, data: any) => api.put(`/admin/navbar/${id}`, data);
export const deleteNavbarItem = (id: string) => api.delete(`/admin/navbar/${id}`);

export const getBanners = () => cachedGet('/public/site/banners/list');
export const getAdminBanners = () => api.get('/admin/banner');
export const getPage = (pageName: string) => api.get(`/public/site/${pageName}`);
export const getCategories = () => cachedGet('/public/categories/list');
export const getPopularStores = () => api.get('/public/popular-stores/list');
export const getFeaturedCoupons = () => api.get('/public/featured-coupons/list');

// Deals
export const getDeals = (params?: any) => cachedGet('/public/deals/list', { params });
export const getDealById = (id: string) => api.get(`/public/deals/details/${id}`);
export const getAdminDeals = () => api.get('/admin/deals');
export const createDeal = (data: any) => api.post('/admin/deals/create', data);
export const updateDeal = (id: string, data: any) => api.put(`/admin/deals/update/${id}`, data);
export const deleteDeal = (id: string) => api.delete(`/admin/deals/delete/${id}`);

// Blog Articles
export const getBlogArticles = () => api.get('/public/blog/list');
export const getBlogArticleBySlug = (slug: string) => api.get(`/public/blog/details/${slug}`);
export const getAdminBlogArticles = () => api.get('/admin/blog');
export const createBlogArticle = (data: any) => api.post('/admin/blog/create', data);
export const updateBlogArticle = (id: string, data: any) => api.put(`/admin/blog/update/${id}`, data);
export const deleteBlogArticle = (id: string) => api.delete(`/admin/blog/delete/${id}`);

// Promo Banners
export const getPromoBanners = (params?: any) => api.get('/public/promo-banners/list', { params });
export const getAdminPromoBanners = () => api.get('/admin/promo-banners');
export const createPromoBanner = (data: any) => api.post('/admin/promo-banners/create', data);
export const updatePromoBanner = (id: string, data: any) => api.put(`/admin/promo-banners/update/${id}`, data);
export const deletePromoBanner = (id: string) => api.delete(`/admin/promo-banners/delete/${id}`);

// Popular Links
export const getPopularLinks = () => api.get('/public/popular-links/list');
export const getAdminPopularLinks = () => api.get('/admin/popular-links');
export const createPopularLink = (data: any) => api.post('/admin/popular-links/create', data);
export const updatePopularLink = (id: string, data: any) => api.put(`/admin/popular-links/update/${id}`, data);
export const deletePopularLink = (id: string) => api.delete(`/admin/popular-links/delete/${id}`);

// Banners (Hero Carousel)
export const createBanner = (data: any) => api.post('/admin/banner/create', data);
export const updateBanner = (id: string, data: any) => api.put(`/admin/banner/update/${id}`, data);
export const deleteBanner = (id: string) => api.delete(`/admin/banner/delete/${id}`);

// Pages
export const getAllPages = () => api.get('/admin/pages');
export const createPage = (data: any) => api.post('/admin/pages/create', data);
export const updatePage = (pageName: string, data: any) => api.put(`/admin/pages/${pageName}/update`, data);
export const deletePage = (id: string) => api.delete(`/admin/pages/${id}`);

// Categories
export const createCategory = (data: any) => api.post('/admin/categories/create', data);
export const updateCategory = (id: string, data: any) => api.put(`/admin/categories/update/${id}`, data);
export const deleteCategory = (id: string) => api.delete(`/admin/categories/delete/${id}`);

// Popular Stores
export const createPopularStore = (data: any) => api.post('/admin/popular-stores/create', data);
export const updatePopularStore = (id: string, data: any) => api.put(`/admin/popular-stores/update/${id}`, data);
export const deletePopularStore = (id: string) => api.delete(`/admin/popular-stores/delete/${id}`);

// Featured Coupons
export const createFeaturedCoupon = (data: any) => api.post('/admin/featured-coupons/create', data);
export const updateFeaturedCoupon = (id: string, data: any) => api.put(`/admin/featured-coupons/update/${id}`, data);
export const deleteFeaturedCoupon = (id: string) => api.delete(`/admin/featured-coupons/delete/${id}`);

// Footer
export const getFooter = () => api.get('/admin/footer');
export const getAllFooterLinksAdmin = () => api.get('/admin/footer');
export const createFooterLink = (data: any) => api.post('/admin/footer/create', data);
export const updateFooterLink = (id: string, data: any) => api.put(`/admin/footer/${id}`, data);
export const deleteFooterLink = (id: string) => api.delete(`/admin/footer/${id}`);

// Tags
export const getTags = () => api.get('/public/tags/list');
export const createTag = (data: any) => api.post('/admin/tags/create', data);
export const updateTag = (id: string, data: any) => api.put(`/admin/tags/update/${id}`, data);
export const deleteTag = (id: string) => api.delete(`/admin/tags/delete/${id}`);

// Upload
export const uploadLogo = (file: File, logoType?: string, oldPublicId?: string) => {
  const formData = new FormData();
  formData.append('logo', file);
  if (logoType) formData.append('logoType', logoType);
  if (oldPublicId) formData.append('oldPublicId', oldPublicId);
  return api.post('/admin/upload/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteLogo = (publicId: string) => api.delete(`/admin/upload/logo/delete/${encodeURIComponent(publicId)}`);
export const uploadBannerImage = (file: File) => uploadLogo(file, 'banner');

// Bulk Delete
export const bulkDeleteStores = (ids: string[], deleteCoupons?: boolean) => api.post('/admin/stores/bulk-delete', { ids, deleteCoupons });
export const bulkDeleteCoupons = (ids: string[]) => api.post('/admin/coupons/bulk-delete', { ids });
export const bulkDeleteDeals = (ids: string[]) => api.post('/admin/deals/bulk-delete', { ids });
export const bulkDeleteBanners = (ids: string[]) => api.post('/admin/banner/bulk-delete', { ids });
export const bulkDeleteTags = (ids: string[]) => api.post('/admin/tags/bulk-delete', { ids });
export const bulkDeleteBlogArticles = (ids: string[]) => api.post('/admin/blog/bulk-delete', { ids });
export const bulkDeleteCategories = (ids: string[]) => api.post('/admin/categories/bulk-delete', { ids });

// Auth
export const adminLogin = (data: { email: string; password: string }) => api.post('/auth/login', data);
export const adminVerify = () => api.get('/auth/verify');
export const adminRefresh = () => api.post('/auth/refresh');
export const adminLogout = () => api.post('/auth/logout');

// Contact Messages
export const submitContactForm = (data: { name: string; email: string; subject: string; message: string }) => api.post('/public/contact/submit', data);
export const getContactMessages = (status?: string) => api.get('/admin/contact-messages', { params: status ? { status } : {} });
export const getContactStats = () => api.get('/admin/contact-messages/stats');
export const updateContactMessage = (id: string, data: any) => api.put(`/admin/contact-messages/${id}`, data);
export const deleteContactMessage = (id: string) => api.delete(`/admin/contact-messages/${id}`);

// Analytics
export const getAnalyticsConfig = () => api.get('/public/site/analytics');
export const updateAnalyticsConfig = (data: any) => api.put('/admin/pages/analytics/update', data);

export default api;
